import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationCircle, 
  FaEye,
  FaEyeSlash,
  FaKey,
  FaLock,
  FaShieldAlt,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";

import { firebaseAuth } from "../services/firebase";

import "./AuthAction.css";

type ActionStatus =
  | "checking"
  | "ready"
  | "saving"
  | "success"
  | "invalid"
  | "unsupported";

const MIN_PASSWORD_LENGTH = 6;

const getFirebaseResetErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error || "");
  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes("auth/expired-action-code") ||
    lowerMessage.includes("expired")
  ) {
    return "Dieser Reset-Link ist abgelaufen. Bitte fordern Sie einen neuen Passwort-Reset-Link an.";
  }

  if (
    lowerMessage.includes("auth/invalid-action-code") ||
    lowerMessage.includes("invalid")
  ) {
    return "Dieser Reset-Link ist ungültig oder wurde bereits verwendet. Bitte fordern Sie einen neuen Link an.";
  }

  if (
    lowerMessage.includes("auth/weak-password") ||
    lowerMessage.includes("weak")
  ) {
    return "Das neue Passwort ist zu schwach. Bitte verwenden Sie mindestens 6 Zeichen.";
  }

  if (
    lowerMessage.includes("auth/user-disabled") ||
    lowerMessage.includes("disabled")
  ) {
    return "Dieses Konto wurde deaktiviert. Bitte kontaktieren Sie den Support.";
  }

  if (message.trim()) {
    return message;
  }

  return "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.";
};

const AuthAction: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get("mode") || "";
  const oobCode = searchParams.get("oobCode") || "";
  const continueUrl = searchParams.get("continueUrl") || "";

  const [status, setStatus] = useState<ActionStatus>("checking");
  const [accountEmail, setAccountEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isResetMode = mode === "resetPassword";

  const pageTitle = useMemo(() => {
    if (status === "success") {
      return "Passwort geändert";
    }

    if (status === "invalid") {
      return "Link ungültig";
    }

    if (status === "unsupported") {
      return "Aktion nicht unterstützt";
    }

    return "Neues Passwort";
  }, [status]);

  const pageText = useMemo(() => {
    if (status === "success") {
      return "Ihr Passwort wurde erfolgreich aktualisiert. Sie können sich jetzt mit Ihrem neuen Passwort einloggen.";
    }

    if (status === "invalid") {
      return "Der Link ist ungültig, abgelaufen oder wurde bereits verwendet. Bitte fordern Sie einen neuen Reset-Link an.";
    }

    if (status === "unsupported") {
      return "Diese E-Mail-Aktion wird auf dieser Seite nicht unterstützt.";
    }

    return "Geben Sie ein neues sicheres Passwort ein, um den Zugriff auf Ihr Dear Language Konto wiederherzustellen.";
  }, [status]);

  useEffect(() => {
    let isCancelled = false;

    const verifyAction = async (): Promise<void> => {
      setErrorMessage("");

      if (!isResetMode) {
        setStatus("unsupported");
        return;
      }

      if (!oobCode) {
        setStatus("invalid");
        setErrorMessage("Der Reset-Code fehlt in der URL.");
        return;
      }

      setStatus("checking");

      try {
        const email = await verifyPasswordResetCode(firebaseAuth, oobCode);

        if (isCancelled) {
          return;
        }

        setAccountEmail(email);
        setStatus("ready");
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setStatus("invalid");
        setErrorMessage(getFirebaseResetErrorMessage(error));
      }
    };

    void verifyAction();

    return () => {
      isCancelled = true;
    };
  }, [isResetMode, oobCode]);

  const handleBackToLogin = (): void => {
    if (continueUrl && continueUrl.startsWith("https://www.dearlanguage.com")) {
      window.location.href = continueUrl;
      return;
    }

    navigate("/courses");
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();

    const cleanPassword = newPassword.trim();
    const cleanConfirmPassword = confirmPassword.trim();

    setErrorMessage("");

    if (!oobCode) {
      setStatus("invalid");
      setErrorMessage("Der Reset-Code fehlt in der URL.");
      return;
    }

    if (cleanPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }

    if (cleanPassword !== cleanConfirmPassword) {
      setErrorMessage("Die Passwörter stimmen nicht überein.");
      return;
    }

    setStatus("saving");

    try {
      await confirmPasswordReset(firebaseAuth, oobCode, cleanPassword);

      setNewPassword("");
      setConfirmPassword("");
      setStatus("success");
    } catch (error) {
      setStatus("ready");
      setErrorMessage(getFirebaseResetErrorMessage(error));
    }
  };

  return (
    <main className="auth-action-page">
      <section className="auth-action-card">
        <div className="auth-action-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span /> 
        </div>

        <button
          type="button"
          className="auth-action-close"
          onClick={() => navigate("/courses")}
          aria-label="Zurück zu den Kursen"
        >
          <FaArrowLeft />
        </button>

        <div className="auth-action-badge">
          <FaShieldAlt />
          <span>Dear Language</span>
        </div>

        <h1 className="auth-action-title">{pageTitle}</h1>

        <p className="auth-action-text">{pageText}</p>

        {status === "checking" && (
          <div className="auth-action-info">
            <FaLock />
            <span>Reset-Link wird geprüft...</span>
          </div>
        )}

        {status === "invalid" && (
          <div className="auth-action-error">
            <FaExclamationCircle />
            <span>
              {errorMessage || "Der Reset-Link ist ungültig oder abgelaufen."}
            </span>
          </div>
        )}

        {status === "unsupported" && (
          <div className="auth-action-error">
            <FaExclamationCircle />
            <span>
              Diese Aktion wird nicht unterstützt. Bitte öffnen Sie einen
              gültigen Passwort-Reset-Link.
            </span>
          </div>
        )}

        {status === "success" && (
          <div className="auth-action-success">
            <FaCheckCircle />
            <span>Ihr neues Passwort wurde gespeichert.</span>
          </div>
        )}

        {(status === "ready" || status === "saving") && (
          <form className="auth-action-form" onSubmit={handleSubmit}>
            <label className="auth-action-label">
              <span>E-Mail-Adresse</span>

              <div className="auth-action-input auth-action-input-readonly">
                <FaEnvelope />
                <input type="email" value={accountEmail} readOnly />
              </div>
            </label>

            <label className="auth-action-label">
              <span>Neues Passwort</span>

              <div className="auth-action-input">
                <FaKey />
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoFocus
                />

                <button
                  type="button"
                  className="auth-action-password-toggle"
                  onClick={() => setShowNewPassword((current) => !current)}
                  aria-label={
                    showNewPassword
                      ? "Passwort ausblenden"
                      : "Passwort anzeigen"
                  }
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>

            <label className="auth-action-label">
              <span>Passwort bestätigen</span>

              <div className="auth-action-input">
                <FaLock />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  autoComplete="new-password"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />

                <button
                  type="button"
                  className="auth-action-password-toggle"
                  onClick={() => setShowConfirmPassword((current) => !current)}
                  aria-label={
                    showConfirmPassword
                      ? "Passwort ausblenden"
                      : "Passwort anzeigen"
                  }
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div> 
            </label>

            {errorMessage && (
              <div className="auth-action-error">
                <FaExclamationCircle />
                <span>{errorMessage}</span>
              </div> 
            )}

            <button
              type="submit"
              className="auth-action-button"
              disabled={status === "saving"}
            >
              {status === "saving" ? (
                <span>Passwort wird gespeichert...</span>
              ) : (
                <>
                  <span>Passwort speichern</span>
                  <FaArrowRight />
                </>
              )}
            </button>
          </form>
        )}

        {(status === "success" ||
          status === "invalid" ||
          status === "unsupported") && (
          <button
            type="button"
            className="auth-action-button"
            onClick={handleBackToLogin}
          >
            <span>Zurück zum Login</span>
            <FaArrowRight />
          </button>
        )}

        <button
          type="button"
          className="auth-action-demo"
          onClick={() => navigate("/courses")}
        >
          Zurück zu <strong>Deutschkurse</strong>
        </button>
      </section>
    </main>
  );
};

export default AuthAction;